import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import {
  FLASH_RATE_PLAN_PRODUCT_TYPE,
  buildCreatedRatePlansFromDrafts,
  getRatePlanDraftsValidationError,
} from './utils';
import type { FlashRatePlanDraft } from './utils';

export interface CreateRatePlansParams {
  ratePlanDrafts: Array<FlashRatePlanDraft>;
  vendorId: string;
}

export interface CreateRatePlansResult {
  createdRatePlans?: Array<App.RatePlan>;
  errorMessage?: string;
  nextDrafts: Array<FlashRatePlanDraft>;
  success: boolean;
}

function normalizeRatePlanName(name: string) {
  return name.trim().toLowerCase();
}

/**
 * Rate plan names are unique per vendor, but svc-reservation reports a collision as a bare
 * "Validation error" with nothing naming the offending plan -- useless to a CM who just needs to
 * know to rename it. Checking the vendor's existing names first turns that into a message that
 * says what clashed and what to do, and avoids a pointless write besides.
 */
async function findDuplicateRatePlanNameError(
  ratePlanDrafts: Array<FlashRatePlanDraft>,
  vendorId: string,
): Promise<string | undefined> {
  const seenInDrafts = new Set<string>();

  for (const draft of ratePlanDrafts) {
    const name = normalizeRatePlanName(draft.ratePlanName);

    if (seenInDrafts.has(name)) {
      return `Two rate plans are both named "${draft.ratePlanName.trim()}". Rename one -- rate plan names must be unique for this vendor.`;
    }

    seenInDrafts.add(name);
  }

  let existingNames: Set<string>;

  try {
    const response = await ReservationService.getRatePlans(vendorId);
    existingNames = new Set(
      ((response?.result ?? []) as Array<{ name?: string }>)
        .map((ratePlan) => (ratePlan.name ? normalizeRatePlanName(ratePlan.name) : ''))
        .filter(Boolean),
    );
  } catch (error) {
    // Only here to give a better message than the backend would -- if the lookup itself fails,
    // fall through and let the create attempt report whatever it reports.
    reportError(error);
    return undefined;
  }

  const clashing = ratePlanDrafts.find((draft) => existingNames.has(normalizeRatePlanName(draft.ratePlanName)));

  return clashing
    ? `A rate plan named "${clashing.ratePlanName.trim()}" already exists for this vendor. Rename it -- rate plan names must be unique.`
    : undefined;
}

/**
 * Creates every rate plan draft, unconditionally, in order, stopping at the first failure. Called
 * exactly once per Submission attempt, and any failure rolls back everything created so far --
 * there's never a legitimate "already created" draft to skip on a fresh call, and treating one as
 * skippable (e.g. from a stale marker left over from a prior, rolled-back attempt) would silently
 * reuse an id that no longer exists server-side. Callers own applying nextDrafts/createdRatePlans/
 * errorMessage to their own state.
 */
export async function createRatePlans({
  ratePlanDrafts,
  vendorId,
}: CreateRatePlansParams): Promise<CreateRatePlansResult> {
  const validationError = getRatePlanDraftsValidationError(ratePlanDrafts);

  if (validationError) {
    return { errorMessage: validationError, nextDrafts: ratePlanDrafts, success: false };
  }

  const duplicateNameError = await findDuplicateRatePlanNameError(ratePlanDrafts, vendorId);

  if (duplicateNameError) {
    return { errorMessage: duplicateNameError, nextDrafts: ratePlanDrafts, success: false };
  }

  const createdPlans: Array<App.RatePlan> = [];
  const nextDrafts = [...ratePlanDrafts];

  try {
    for (let index = 0; index < nextDrafts.length; index += 1) {
      const draft = nextDrafts[index];

      const response = await ReservationService.createRatePlan({
        id_salesforce_external: vendorId,
        name: draft.ratePlanName.trim(),
        cancellation_policy: draft.ratePlanCancellationPolicy.trim(),
        status: draft.ratePlanStatus.trim(),
        product_type: FLASH_RATE_PLAN_PRODUCT_TYPE,
        rate_type: draft.ratePlanRateType.trim(),
      });

      if (!response?.result?.id) {
        throw new Error('Unexpected response from createRatePlan');
      }

      nextDrafts[index] = {
        ...draft,
        createdRatePlanId: response.result.id,
      };
      createdPlans.push(response.result);
    }

    return {
      createdRatePlans: buildCreatedRatePlansFromDrafts([], createdPlans, nextDrafts),
      nextDrafts,
      success: true,
    };
  } catch (error) {
    reportError(error);
    const createdCount = createdPlans.length;

    return {
      createdRatePlans: createdCount > 0 ? buildCreatedRatePlansFromDrafts([], createdPlans, nextDrafts) : undefined,
      errorMessage:
        createdCount > 0
          ? `Created ${createdCount} of ${nextDrafts.length} rate plans. The whole attempt is rolled back regardless -- fix the issue and try again. ${getErrorMessage(error)}`
          : `Unable to create rate plans. ${getErrorMessage(error)}`,
      nextDrafts,
      success: false,
    };
  }
}
